import { Outlet, useParams } from 'react-router'
import { observer } from 'mobx-react-lite'
import { Box, Icon, Status, Tooltip, Typography } from 'shared/ui'
import { truthy } from 'shared/lib'
import { MenuItemNav } from 'shared/ui/Menu/ui/MenuItem/MenuItemNav/MenuItemNav'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { getStateAccessAIAutomation } from 'widgets/Menu/ui/MenuAIAutomation/lib/getPathAIAutomation'
import { EnumMenuAIAutomationItemsKey } from 'widgets/Menu/ui/MenuAIAutomation/constants/menuItems'
import { InboxesRoutes } from 'pages/settings/pages/inboxes/route/type'
import { InboxesPath } from 'pages/settings/pages/inboxes/route/inboxesPath'
import { useInboxLayerContext } from 'pages/settings/pages/inboxes/context/inboxLayerContext'
import styles from './styles.module.scss'

export const InboxSettingsLayout = observer(() => {
  const { featureFlagsStore } = useEntitiesContext()
  const { id } = useParams()
  const base = `/${InboxesPath.root}/${id}/${InboxesRoutes.settings}/`
  const canManageTextBots = getStateAccessAIAutomation(
    EnumMenuAIAutomationItemsKey.chatbot,
    useEntitiesContext()
  )
  const { currentTeam, isAircallOnlyTeam, isCallFlowEnabled, isAutoRechargeRequired } =
    useInboxLayerContext()
  const canManageInboxPageCalling = featureFlagsStore.settings_inboxes_manage_inbox_calling_v2

  const messagingItems = [
    { key: InboxesRoutes.general, title: 'General' },
    { key: InboxesRoutes.smartInbox, title: 'Smart inbox' },
    { key: InboxesRoutes.conversationAssignment, title: 'Conversation assignment' },
    { key: InboxesRoutes.businessHours, title: 'Business hours' },
  ]
  const shouldDisableSmartCallFlow = !currentTeam?.number?.voice

  if (canManageTextBots) {
    messagingItems.push({ key: InboxesRoutes.aiAutomation, title: 'AI automation' })
  }

  const messagingItemsUi = messagingItems.map(({ key, title }) => {
    const path = `${base}${InboxesRoutes.messaging}/${key}`
    return (
      <MenuItemNav
        key={path}
        type={'nav'}
        to={path}
        label={
          <Typography
            variant={'body-md-regular'}
            pointer
            className={styles.menuItemLabel}
            ariaLabel={title}
          >
            {title}
          </Typography>
        }
        variantActiveState={'neutral'}
      />
    )
  })

  const callingItems = [
    { key: InboxesRoutes.general, title: 'General' },
    {
      key: InboxesRoutes.smartCallFlow,
      title: 'Smart call flow',
      disabled: !canManageInboxPageCalling,
    },
  ]
    .filter(truthy)
    .map(({ key, title, disabled }) => {
      const path = `${base}${InboxesRoutes.calling}/${key}`
      return (
        <Box position={'relative'} key={path}>
          <MenuItemNav
            key={path}
            type={'nav'}
            to={path}
            disabledVueRouting
            label={
              <Box display={'flex'} alignItems={'center'} gap={'4px'}>
                <Typography
                  variant={'body-md-regular'}
                  pointer
                  className={styles.menuItemLabel}
                  ariaLabel={title}
                  ellipsis
                >
                  {title}
                </Typography>
                {disabled && !canManageInboxPageCalling ? (
                  <Status
                    title={'Coming soon'}
                    intent={'positive'}
                    emphasis={'high'}
                    size={'label'}
                    className={styles.statusComingSoon}
                  />
                ) : key === InboxesRoutes.smartCallFlow ? (
                  <Status title={'New'} intent={'purple'} emphasis={'high'} size={'label'} />
                ) : undefined}
              </Box>
            }
            disabled={Boolean(disabled)}
            variantActiveState={'neutral'}
            className={styles.menuItem}
          />

          {key === InboxesRoutes.smartCallFlow &&
            canManageInboxPageCalling &&
            (shouldDisableSmartCallFlow ? (
              <Box className={styles.alertIconOverlay}>
                <Tooltip
                  label={'Smart call flow disabled due inbound calls option disabled'}
                  breakWord
                  width={199}
                  placement='top'
                >
                  <Icon icon={'alertTriangleYellow'} />
                </Tooltip>
              </Box>
            ) : !isCallFlowEnabled ? (
              <Box className={styles.alertIconOverlay}>
                <Tooltip
                  label={
                    'Incoming calls to this inbox are being dropped. Enable call flow to receive them.'
                  }
                  breakWord
                  width={199}
                  placement='top'
                >
                  <Icon icon={'alertTriangleYellow'} />
                </Tooltip>
              </Box>
            ) : isAutoRechargeRequired ? (
              <Box className={styles.alertIconOverlay}>
                <Tooltip
                  label={
                    <>
                      Auto-Recharge required. <br /> To use call forwarding, <br />
                      enable auto-recharge
                    </>
                  }
                  breakWord
                  placement='top'
                >
                  <Icon icon={'alertTriangleYellow'} />
                </Tooltip>
              </Box>
            ) : null)}
        </Box>
      )
    })
  return (
    <Box display={'flex'} gap={5} height={'100%'} padding={'48px 0 0'} marginTop={0.5}>
      <Box display={'flex'} flexDirection={'column'} gap={'20px'} className={styles.menu}>
        <Box display={'flex'} flexDirection={'column'}>
          <Typography
            variant={'button-small'}
            color={'tertiary'}
            ariaLabel={'Messaging'}
            className={styles.menuTitle}
          >
            Messaging
          </Typography>
          {messagingItemsUi}
        </Box>
        {!isAircallOnlyTeam && (
          <Box display={'flex'} flexDirection={'column'}>
            <Typography
              variant={'button-small'}
              color={'tertiary'}
              ariaLabel={'Calling'}
              className={styles.menuTitle}
            >
              Calling
            </Typography>
            {callingItems}
          </Box>
        )}
      </Box>

      <div className={styles.content}>
        <Outlet />
      </div>
    </Box>
  )
})
